import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import ReactDOM from 'react-dom';
import {
    BookOpen, Search, ArrowLeft, Clock, MessageCircle, Send,
    X, ChevronLeft, Plus, Trash2, Check, Crown, CornerDownRight, Smile, UserCheck, Trophy,
    Loader2, Globe, Calendar, Eye, Sparkles, Users
} from 'lucide-react';
import { WatchdogGuard } from '../components/WatchdogGuard';
import { FeedSkeleton } from '../components/SkeletonLoader';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';
import AvatarRenderer from '../components/AvatarRenderer';
import { profileService } from '../services/profileService';
import { socialService } from '../services/socialService';
import { bookService } from '../services/bookService';
import { storageService } from '../services/storageService';
import { dbManager } from '../services/db_manager';
import { cacheManager } from '../services/cacheManager';
import { getLanguageName } from '../services/languageConstants';
import { useMention } from '../hooks/useMention';
import { useTranslation } from 'react-i18next';
import MentionDropdown from '../components/MentionDropdown';
import { AppBackButton } from '../components/AppBackButton';
import { AnimatedIcon } from '../components/AnimatedIcon';
import { CommunityLinks } from '../components/CommunityLinks';
import { StudyPageHeader } from '../components/StudyPageHeader';
import T from '../components/T';


// ─── Types ────────────────────────────────────────────────────────────────────

interface BookClub {
    id: string;
    creator_id: string;
    book_id_reference: string;
    book_title: string;
    book_cover: string | null;
    book_url: string;
    book_format: string;
    book_language: string | null;
    book_author: string | null;
    deadline: string;
    status: 'ativo' | 'finalizado';
    created_at: string;
    role?: 'criador' | 'membro';
    member_status?: 'pending' | 'accepted' | 'declined';
    invited_by?: string;
}

interface ClubMember {
    id: string;
    club_id: string;
    user_id: string;
    invited_by: string;
    status: 'pending' | 'accepted' | 'declined';
    joined_at: string | null;
    shelf_book_id: string | null;
    profile?: { display_name: string; avatar_url: string | null; username: string };
}

interface ClubComment {
    id: string;
    club_id: string;
    user_id: string;
    parent_id: string | null;
    content: string;
    is_spoiler?: boolean | number;
    created_at: string;
    profile?: { display_name: string; avatar_url: string | null };
    reactions?: { emoji: string; count: number; reacted: boolean }[];
    replies?: ClubComment[];
}

interface LibraryBook {
    id: string;
    title: string;
    author: string;
    language: string;
    url: string;
    cover: string;
    format: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const LIBRARY_HTML_URL = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/library-capas-atualizadas.json";
const R2_HTML_BASE = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/biblioteca-online-htmls/";
const R2_COVER_BASE = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/capas-atualizadas/";
const R2_PDF_BASE = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/pdfs/";
const REACTION_EMOJIS = ['❤️', '🔥', '👏', '😂', '🤩', '🤔', '😢', '💡'];

// ─── Language helper ─────────────────────────────────────────────────────────
const LANG_LABELS: Record<string, string> = {
    en: 'EN', 'en-US': 'EN', 'en-GB': 'EN',
    pt: 'PT', 'pt-BR': 'PT-BR', 'pt-PT': 'PT',
    fr: 'FR', es: 'ES', de: 'DE', it: 'IT',
    nl: 'NL', ru: 'RU', ja: 'JA', zh: 'ZH',
    ar: 'AR', la: 'LA', fi: 'FI', sv: 'SV',
    pl: 'PL', cs: 'CS', hu: 'HU', ro: 'RO',
};

const ISO_FLAG_MAP: Record<string, string> = {
    'af': '🇿🇦', 'ar': '🇸🇦', 'arp': '🏹', 'bg': '🇧🇬', 'bo': '☸️', 'br': '⚓',
    'ca': '🏔️', 'ceb': '🇵🇭', 'cs': '🇨🇿', 'cy': '🏴󠁧󠁢󠁷󠁬ês󠁿', 'da': '🇩🇰', 'de': '🇩🇪',
    'el': '🇬🇷', 'en': '🇺🇸', 'enm': '📜', 'eo': '🟢', 'es': '🇪🇸', 'et': '🇪🇪',
    'fa': '🇮🇷', 'fi': '🇫🇮', 'fr': '🇫🇷', 'fur': '⛰️', 'fy': '🇳🇱', 'ga': '🇮🇪',
    'gl': '🌾', 'gla': '🏴󠁧󠁢󠁳󠁣󠁴󠁿', 'he': '🇮🇱', 'hu': '🇭🇺', 'ia': '🌐', 'ilo': '🇵🇭',
    'is': '🇮🇸', 'it': '🇮🇹', 'iu': '❄️', 'ja': '🇯🇵', 'la': '🏛️', 'mi': '🌀',
    'myn': '🗿', 'na': '🇳🇷', 'nah': '🌋', 'nap': '🍕', 'nl': '🇳🇱', 'no': '🇳🇴',
    'oc': '🌿', 'oj': '🌲', 'pl': '🇵🇱', 'pt': '🇧🇷', 'rmq': '💃', 'ro': '🇷🇴',
    'ru': '🇷🇺', 'sa': '🕉️', 'sco': '🏴󠁧󠁢󠁳󠁣󠁴󠁿', 'sl': '🇸🇮', 'sr': '🇷🇸', 'sv': '🇸🇪',
    'te': '🇮🇳', 'tl': '🇵🇭', 'yi': '🕎', 'zh': '🇨🇳', 'und': '❓'
};
const langLabel = (code: string) => LANG_LABELS[code] || (code ? code.toUpperCase().slice(0, 5) : '?');

// ─── Helper ───────────────────────────────────────────────────────────────────

const daysLeft = (deadline: string): number => {
    const diff = new Date(deadline).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

const formatDate = (d: string, locale: string = 'pt-BR') =>
    new Date(d).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });

const timeAgo = (d: string) => {
    const diff = Date.now() - new Date(d).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'agora';
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const UserAvatar: React.FC<{ url?: string | null; name: string; size?: number }> = ({ url, name, size = 36 }) => (
    <div style={{ width: size, height: size, flexShrink: 0 }}>
        {url ? (
            <img src={url} alt={name} className="rounded-full object-cover w-full h-full border border-white/20" />
        ) : (
            <div className="rounded-full bg-ink flex items-center justify-center text-surface font-semibold w-full h-full"
                style={{ fontSize: size * 0.38 }}>
                {(name || "?").charAt(0).toUpperCase()}
            </div>
        )}
    </div>
);

const BookCover: React.FC<{
    title: string;
    author?: string | null;
    coverUrl?: string | null;
    isSmall?: boolean;
}> = ({ title, author, coverUrl, isSmall = false }) => {
    const [hasError, setHasError] = useState(false);
    const { isDarkMode } = useTheme();
    
    // R2 Prepending logic
    let displayUrl = coverUrl;
    if (displayUrl && !displayUrl.startsWith('http') && !displayUrl.startsWith('generated://')) {
        displayUrl = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/capas-atualizadas/${displayUrl}`;
        // Ensure extension if missing
        if (!displayUrl.toLowerCase().endsWith('.jpg') && !displayUrl.toLowerCase().endsWith('.png')) {
            displayUrl += '.jpg';
        }
    }

    const isGenerated = !displayUrl || displayUrl.startsWith('generated://');

    if (!isGenerated && !hasError) {
        return (
            <div className="w-full h-full relative group overflow-hidden bg-slate-200 dark:bg-slate-800">
                <img
                    src={displayUrl!}
                    alt={title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    onError={() => setHasError(true)}
                    loading="lazy"
                />
                {!isSmall && (
                    <div className="absolute inset-0 bg-black/50 p-4 pl-[10%] pb-6 flex flex-col justify-end text-left z-20">
                        <h4 className="text-white font-normal text-[11px] leading-tight line-clamp-2 drop-shadow-md uppercase font-mono tracking-[0.14em]">{title}</h4>
                        {author && <p className="text-slate-300 font-bold text-[9px] mt-1 line-clamp-1 opacity-90">{author}</p>}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={`w-full h-full flex flex-col relative overflow-hidden group/fallback border border-slate-200/50 dark:border-white/[0.06] rounded-xl ${isDarkMode ? 'bg-gradient-to-br from-slate-800 to-slate-950' : 'bg-gradient-to-br from-slate-50 to-slate-200'}`}>
            <div className="absolute -top-10 -left-10 w-32 h-32 bg-primary/5 rounded-full blur-3xl group-hover/fallback:bg-primary/10 transition-colors duration-700" />
            
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20 group-hover/fallback:scale-110 transition-transform duration-500">
                <BookOpen className="text-primary" size={isSmall ? 48 : 96} />
            </div>

            {isSmall && (
                <div className="relative z-10 flex flex-col h-full justify-center p-4">
                    <h4 className={`text-[8px] font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'} line-clamp-4 uppercase tracking-tight leading-[1.3] drop-shadow-sm group-hover/fallback:text-primary transition-colors`}>
                        {title}
                    </h4>
                </div>
            )}

            {!isSmall && (
                <div className="absolute inset-0 bg-black/50 p-4 pl-[10%] pb-6 flex flex-col justify-end text-left z-20">
                    <h4 className="text-white font-normal text-[11px] leading-tight line-clamp-2 drop-shadow-md uppercase font-mono tracking-[0.14em]">{title}</h4>
                    {author && <p className="text-slate-300 font-bold text-[9px] mt-1 line-clamp-1 opacity-90">{author}</p>}
                </div>
            )}

            <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/5 to-white/10 opacity-0 group-hover/fallback:opacity-100 transition-opacity pointer-events-none" />
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// COMMENT COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const Comment: React.FC<{
    comment: ClubComment;
    userId: string;
    onReact: (commentId: string, emoji: string) => void;
    onReply: (comment: ClubComment) => void;
    onDelete: (commentId: string) => void;
    isReply?: boolean;
}> = ({ comment, userId, onReact, onReply, onDelete, isReply = false }) => {
    const [showReactions, setShowReactions] = useState(false);
    const [isRevealed, setIsRevealed] = useState(false);
    const [showSpoilerConfirm, setShowSpoilerConfirm] = useState(false);
    const { t } = useTranslation();

    const isSpoiler = (comment.is_spoiler === true || comment.is_spoiler === 1);
    // User can see their own spoilers or if revealed
    const needsReveal = isSpoiler && !isRevealed && comment.user_id !== userId;

    return (
        <div className={`flex gap-3 ${isReply ? 'ml-10 mt-3' : ''}`}>
            <UserAvatar url={comment.profile?.avatar_url} name={comment.profile?.display_name || comment.profile?.username || '?'} size={isReply ? 28 : 36} />
            <div className="flex-1 min-w-0">
                <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl rounded-tl-sm px-4 py-3 relative overflow-hidden">
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-[11px] font-semibold dark:text-ink">{comment.profile?.display_name || comment.profile?.username || 'Participante'}</span>
                        <div className="flex items-center gap-2">
                            {isSpoiler && (
                                <span className="text-[9px] font-medium text-warn bg-warn/10 px-2 py-0.5 rounded-full border border-warn/20">
                                    Spoiler
                                </span>
                            )}
                            <span className="text-[10px] text-slate-400">{timeAgo(comment.created_at)}</span>
                        </div>
                    </div>
                    
                    <div className="relative">
                        <p className={`text-[13px] text-slate-700 dark:text-slate-200 leading-relaxed transition-all duration-500 ${needsReveal ? 'blur-md select-none' : ''}`}>
                            {comment.content}
                        </p>

                        {needsReveal && (
                            <>
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-auto">
                                    <button 
                                        onClick={() => setShowSpoilerConfirm(true)}
                                        className="bg-white/80 dark:bg-slate-950/80 backdrop-blur-md p-2 rounded-full hover:scale-110 active:scale-95 transition-all text-primary border border-primary/20"
                                        title="Exibir spoiler"
                                    >
                                        <Eye size={18} />
                                    </button>
                                </div>
                                {showSpoilerConfirm && ReactDOM.createPortal(
                                    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-300 pointer-events-auto">
                                        <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] p-6 rounded-[24px] w-full max-w-sm mx-auto animate-in zoom-in-95 duration-300">
                                            <div className="w-16 h-16 bg-warn/10 text-warn rounded-full flex items-center justify-center mx-auto mb-4">
                                                <Eye size={32} />
                                            </div>
                                            <h3 className="text-lg font-semibold text-center mb-2 dark:text-ink"><T k="ui.club.spoilerTitle" /></h3>
                                            <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-6"><T k="ui.club.spoiler" /></p>
                                            <div className="flex gap-3">
                                                <button 
                                                    onClick={() => { setIsRevealed(true); setShowSpoilerConfirm(false); }}
                                                    className="flex-1 bg-primary text-slate-900 font-semibold py-3 rounded-xl hover:opacity-90 active:scale-95 transition-all"
                                                >
                                                    Sim
                                                </button>
                                                <button 
                                                    onClick={() => setShowSpoilerConfirm(false)}
                                                    className="flex-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold py-3 rounded-xl hover:opacity-90 active:scale-95 transition-all"
                                                >
                                                    Não
                                                </button>
                                            </div>
                                        </div>
                                    </div>,
                                    document.body
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 mt-1.5 pl-1 flex-wrap">
                    {/* Reactions */}
                    {comment.reactions && comment.reactions.length > 0 && (
                        <div className="flex gap-1">
                            {comment.reactions.map(r => (
                                <button
                                    key={r.emoji}
                                    onClick={() => onReact(comment.id, r.emoji)}
                                    className={`flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[11px] border transition-all ${r.reacted ? 'bg-primary/10 border-primary/80 text-primary' : 'bg-slate-100 dark:bg-slate-800 border-black/[0.06] dark:border-slate-700 text-slate-500'}`}
                                >
                                    {r.emoji} <span className="font-bold">{r.count}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="relative">
                        <button
                            onClick={() => setShowReactions(!showReactions)}
                            className="text-slate-400 hover:text-primary transition-colors flex items-center gap-1 text-[11px] font-bold"
                        >
                            <Smile size={12} /> {t('community.book_club.club.react', 'Reagir')}
                        </button>
                        {showReactions && (
                            <div className="absolute bottom-full left-0 mb-1 bg-surface border border-black/[0.06] dark:border-slate-700 rounded-2xl p-2 flex gap-1.5 z-10">
                                {REACTION_EMOJIS.map(e => (
                                    <button key={e} onClick={() => { onReact(comment.id, e); setShowReactions(false); }}
                                        className="text-lg hover:scale-125 transition-transform">{e}</button>
                                ))}
                            </div>
                        )}
                    </div>

                    {!isReply && (
                        <button onClick={() => onReply(comment)}
                            className="text-slate-400 hover:text-primary transition-colors flex items-center gap-1 text-[11px] font-bold">
                            <CornerDownRight size={11} /> {t('community.book_club.club.reply', 'Responder')}
                        </button>
                    )}

                    {comment.user_id === userId && (
                        <button onClick={() => onDelete(comment.id)}
                            className="text-slate-400 hover:text-bad transition-colors flex items-center gap-1 text-[11px] font-bold">
                            <Trash2 size={11} /> {t('community.book_club.club.delete', 'Excluir')}
                        </button>
                    )}
                </div>

                {/* Replies */}
                {comment.replies && comment.replies.length > 0 && (
                    <div className="mt-2 space-y-2">
                        {comment.replies.map(r => (
                            <Comment key={r.id} comment={r} userId={userId} onReact={onReact} onReply={onReply} onDelete={onDelete} isReply />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

type View = 'landing' | 'list' | 'detail' | 'comment-feed';

const CommunityPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { user } = useAuth();
    const { accentColor } = useTheme();
    const { friends, profile } = useData();
    const location = useLocation();
    const navigate = useNavigate();

    const [view, setView] = useState<View>('landing');
    const [clubs, setClubs] = useState<BookClub[]>([]);
    const [selectedClub, setSelectedClub] = useState<BookClub | null>(null);
    const [members, setMembers] = useState<ClubMember[]>([]);
    const [creatorProfile, setCreatorProfile] = useState<{ display_name?: string; avatar_url?: string; username?: string } | null>(null);
    const [comments, setComments] = useState<ClubComment[]>([]);
    const [loading, setLoading] = useState(false);

    // Create Club Modal
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [libraryBooks, setLibraryBooks] = useState<LibraryBook[]>([]);
    const [bookSearch, setBookSearch] = useState('');
    const [selectedLanguage, setSelectedLanguage] = useState('');
    const [selectedBook, setSelectedBook] = useState<LibraryBook | null>(null);
    const [deadline, setDeadline] = useState('');
    const [selectedFriendIds, setSelectedFriendIds] = useState<string[]>([]);
    const [creating, setCreating] = useState(false);
    const [loadingLibrary, setLoadingLibrary] = useState(false);

    // Comment Feed
    const {
        text: commentText,
        setText: setCommentText,
        handleKeyDown,
        handleSelectMention,
        handleCursorChange,
        mentionResults,
        selectedIndex,
        showDropdown
    } = useMention('', friends);
    const [replyTo, setReplyTo] = useState<ClubComment | null>(null);
    const [sendingComment, setSendingComment] = useState(false);
    const [isCommentSpoiler, setIsCommentSpoiler] = useState(false);
    
    // UI Modals & Settings
    const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
    const [clubToDelete, setClubToDelete] = useState<string | null>(null);
    const [showLimitModal, setShowLimitModal] = useState(false);
    const [deadlineTime, setDeadlineTime] = useState('23:59');

    // ── Fetch user's clubs ────────────────────────────────────────────────────

    const fetchClubs = useCallback(async () => {
        if (!user || !user.id) {
            setLoading(false);
            return;
        }
        setLoading(true);

        const cacheKey = `data_clubs_${user.id}`;
        await cacheManager.swr(
            cacheKey,
            async () => {
                const { created, memberships } = await socialService.getClubs(user.id);

                const createdClubs: BookClub[] = created.map((c: any) => ({ ...c, role: 'criador' as const }));

                const memberClubs: BookClub[] = memberships
                    .map((m: any) => ({
                        ...(m.book_clubs || {}),
                        role: 'membro' as const,
                        member_status: m.status,
                        invited_by: m.invited_by
                    }))
                    .filter((m: any) => m.id);

                // Deduplicate
                const merged = [...createdClubs];
                memberClubs.forEach(mc => {
                    if (!merged.find(c => c.id === mc.id)) merged.push(mc);
                });

                return merged;
            },
            (data: BookClub[], isCached: boolean) => {
                setClubs(data);
                if (!isCached) setLoading(false);
            }
        );
        setLoading(false);
    }, [user]);

    useEffect(() => {
        if (view === 'list') fetchClubs();
    }, [view, fetchClubs]);

    // ── Fetch Club Detail ─────────────────────────────────────────────────────

    const fetchClubDetail = async (club: BookClub) => {
        setSelectedClub(club);

        const isCreator = club.creator_id === user?.id;
        const isAcceptedMember = club.role === 'membro' && club.member_status === 'accepted';

        // Redirect to comment feed if established member/creator
        if (isCreator || isAcceptedMember) {
            setView('comment-feed');
            fetchComments(club.id);
        } else {
            // Pending members go to detail to accept/decline
            setView('detail');
        }


        const cacheKey = `data_club_detail_${club.id}`;
        
        await cacheManager.swr(
            cacheKey,
            async () => {
                const membersResponse = await socialService.getClubMembers(club.id);
                const rawMembers = Array.isArray(membersResponse) ? membersResponse : (membersResponse?.data || []);
                const creator = await profileService.getProfile(club.creator_id);
                
                return {
                    members: rawMembers,
                    creator
                };
            },
            (data: { members: any[], creator: any }) => {
                setMembers(data.members);
                setCreatorProfile(data.creator);
            },
            30000
        );
    };

    // ── Fetch Comments ────────────────────────────────────────────────────────

    const fetchComments = async (clubId: string) => {
        const cacheKey = `data_club_comments_${clubId}`;
        await cacheManager.swr(
            cacheKey,
            async () => {
                const response = await socialService.getComments(clubId, user?.id);
                const rawComments = Array.isArray(response) ? response : (response?.data || []);

                // Map 'profiles' from D1 join into 'profile' property expected by UI
                const mappedComments = rawComments.map(c => ({
                    ...c,
                    profile: c.profiles || c.profile
                }));

                // Group replies
                const topLevel = mappedComments.filter((c: any) => !c.parent_id);
                const replies = mappedComments.filter((c: any) => c.parent_id);

                replies.forEach((r: any) => {
                    const parent = topLevel.find((t: any) => t.id === r.parent_id);
                    if (parent) {
                        if (!parent.replies) parent.replies = [];
                        parent.replies.push(r);
                    }
                });

                // Sort replies
                topLevel.forEach((t: any) => {
                    if (t.replies) {
                        t.replies.sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
                    }
                });

                return topLevel as ClubComment[];
            },
            (data: ClubComment[]) => {
                setComments(data);
            },
            10000 // 10s debounce for comments
        );
    };

    // ── Library Books ─────────────────────────────────────────────────────────

    const fetchLibraryBooks = async () => {
        setLoadingLibrary(true);
        const cacheKey = `data_library_catalog_v3`;
        await cacheManager.swr(
            cacheKey,
            async () => {
                const res = await fetch(LIBRARY_HTML_URL, {
                    headers: { 'Accept': 'application/json' }
                });

                if (!res.ok) throw new Error(`HTTP error ${res.status}`);

                const text = await res.text();
                const cleanText = text.replace(/: NaN/g, ': null');
                const rawData = JSON.parse(cleanText);
                const list = Array.isArray(rawData) ? rawData : [];

                const normalized = list.map((item: any) => {
                    try {
                        const id = String(item.id || item.gutenberg_id || item.ID || "").trim();
                        if (!id || id === "undefined") return null;

                        // Force R2 path to ensure consistency
                        const fileUrl = `${R2_HTML_BASE}${id}.html`;

                        // Language normalization
                        const rawLang = String(item.language || item.idioma || "und").trim();
                        const normalizedLang = rawLang.split(/[;,\s]/)[0].toLowerCase();

                        // Cover logic
                        let cover = String(item.cover || item.capa || "").trim();
                        if (cover.toLowerCase() === "vazio" || !cover) {
                            cover = `${R2_COVER_BASE}${id}.jpg`;
                        }

                        return {
                            id,
                            title: item.title || item.titulo || "Título Desconhecido",
                            author: item.author || item.autor || "Autor Desconhecido",
                            language: normalizedLang,
                            url: fileUrl,
                            cover: cover,
                            format: 'html'
                        };
                    } catch (e) {
                        return null;
                    }
                }).filter(b => b !== null);

                return normalized as LibraryBook[];
            },
            (data: LibraryBook[], isCached: boolean) => {
                setLibraryBooks(data);
                if (!isCached) setLoadingLibrary(false);
            },
            60 * 60 * 1000 // 1 hour debounce for global static catalog
        );
        setLoadingLibrary(false);
    };

    useEffect(() => {
        if (showCreateModal && libraryBooks.length === 0) fetchLibraryBooks();
    }, [showCreateModal]);

    const availableLanguages = React.useMemo(() => {
        const langs = new Set<string>();
        libraryBooks.forEach(b => { if (b.language) langs.add(b.language); });
        return Array.from(langs).sort((a, b) => getLanguageName(a).localeCompare(getLanguageName(b)));
    }, [libraryBooks]);

    const filteredBooks = libraryBooks.filter(b => {
        const matchesLang = !selectedLanguage || b.language === selectedLanguage;
        const matchesSearch = bookSearch.length < 2 || 
            b.title.toLowerCase().includes(bookSearch.toLowerCase()) ||
            b.author.toLowerCase().includes(bookSearch.toLowerCase());
        return matchesLang && matchesSearch;
    });

    // ── Create Club ───────────────────────────────────────────────────────────

    // ── Shared helper: add a club book to a user's shelf ───────────────────────
    const addBookToShelf = async (
        targetUserId: string,
        clubId: string,
        bookInfo: {
            title: string;
            author: string | null;
            cover: string | null;
            url: string;
            language: string | null;
            format: string;
        }
    ) => {
        const shelfBookId = `club_${clubId}_${targetUserId}`;

        // Local storage copy (for instant UI)
        try {
            storageService.saveBook({
                id: shelfBookId,
                title: bookInfo.title,
                author: bookInfo.author || 'Desconhecido',
                coverUrl: bookInfo.cover || '',
                fileUrl: bookInfo.url,
                lastPage: 1,
                totalPages: 0,
                dateAdded: Date.now(),
                category: 'clube_do_livro',
                language: bookInfo.language || '',
                format: bookInfo.format || 'html',
            } as any);
        } catch (err) {
            console.error("CommunityPage: Erro ao salvar livro no storage local", err);
        }

        // D1 copy
        if (user && !user.isGuest) {
            try {
                await bookService.saveBook(targetUserId, {
                    custom_id: shelfBookId,
                    title: bookInfo.title,
                    author: bookInfo.author || 'Desconhecido',
                    cover_url: bookInfo.cover || '',
                    file_url: bookInfo.url,
                    file_type: bookInfo.format === 'pdf' ? 'pdf' : 'html',
                    language: bookInfo.language || 'und',
                    is_favorite: false,
                    category: 'clube_do_livro'
                });
            } catch (err) {
                console.error('addBookToShelf D1 error:', err);
            }
        }
        return shelfBookId;
    };

    const handleCreateClub = async () => {
        if (!user || !selectedBook || !deadline || selectedFriendIds.length === 0) return;

        // Freemium limit check
        if (profile?.subscription_tier === 'free' && clubs.filter(c => c.status === 'ativo' && (c.role === 'criador' || c.member_status === 'accepted')).length >= 1) {
            setShowLimitModal(true);
            return;
        }

        setCreating(true);

        try {
            const clubId = await socialService.createClub(user.id, {
                book_id_reference: selectedBook.id,
                book_title: selectedBook.title,
                book_cover: selectedBook.cover,
                book_url: selectedBook.url,
                book_format: selectedBook.format || 'html',
                book_language: selectedBook.language,
                book_author: selectedBook.author,
                deadline: new Date(`${deadline}T${deadlineTime}:00`).toISOString(),
            });

            // Add the book to the CREATOR's shelf
            await addBookToShelf(user.id, clubId, {
                title: selectedBook.title,
                author: selectedBook.author,
                cover: selectedBook.cover,
                url: selectedBook.url,
                language: selectedBook.language,
                format: selectedBook.format || 'html',
            });

            // Invite selected friends
            for (const friendId of selectedFriendIds.slice(0, 5)) {
                await socialService.inviteMember(clubId, friendId, user.id);
            }

            setCreating(false);
            setShowCreateModal(false);
            setSelectedBook(null);
            setBookSearch('');
            setDeadline('');
            setSelectedFriendIds([]);
            cacheManager.clear(`data_clubs_${user.id}`);
            fetchClubs();

            // Fetch details for the new club
            const clubRes = await socialService.getClubById(clubId);
            if (clubRes) fetchClubDetail({ ...clubRes, role: 'criador' });
        } catch (err) {
            console.error("Error creating club:", err);
            setCreating(false);
        }
    };

    const handleAcceptInvite = async (club: BookClub) => {
        if (!user) return;

        // Freemium limit check
        if (profile?.subscription_tier === 'free' && clubs.filter(c => c.status === 'ativo' && (c.role === 'criador' || c.member_status === 'accepted')).length >= 1) {
            setShowLimitModal(true);
            return;
        }

        try {
            // Add book to the MEMBER's shelf (local + D1)
            const shelfBookId = await addBookToShelf(user.id, club.id, {
                title: club.book_title,
                author: club.book_author,
                cover: club.book_cover,
                url: club.book_url,
                language: club.book_language,
                format: club.book_format,
            });

            // Update membership in D1
            await socialService.updateMemberStatus(club.id, user.id, 'accepted', club.invited_by || '', shelfBookId);

            // Update local state immediately so the UI reflects the change
            setSelectedClub(prev => prev ? { ...prev, member_status: 'accepted' } : prev);
            setMembers(prev => prev.map(m =>
                m.user_id === user.id ? { ...m, status: 'accepted' } : m
            ));
            cacheManager.clear(`data_clubs_${user.id}`);
            fetchClubs();
        } catch (err) {
            console.error('Accept invite error:', err);
        }
    };

    const handleDeclineInvite = async (club: BookClub) => {
        if (!user) return;
        try {
            await socialService.updateMemberStatus(club.id, user.id, 'declined', club.invited_by || '');
            setView('list');
            setSelectedClub(null);
            cacheManager.clear(`data_clubs_${user.id}`);
            fetchClubs();
        } catch (err) {
            console.error('Decline invite error:', err);
        }
    };

    const handleSendComment = async () => {
        if (!user || !selectedClub || !commentText.trim()) return;
        
        const originalText = commentText.trim();
        const optimisticId = `temp_${Date.now()}`;
        const newComment: ClubComment = {
            id: optimisticId,
            club_id: selectedClub.id,
            user_id: user.id,
            parent_id: replyTo?.id || null,
            content: originalText,
            is_spoiler: isCommentSpoiler,
            created_at: new Date().toISOString(),
            profile: {
                display_name: profile?.display_name || profile?.username || user?.email?.split('@')[0] || 'Você',
                avatar_url: profile?.avatar_url || null
            },
            reactions: [],
            replies: []
        };

        // Optimistic update: Append to the end (bottom) as requested
        if (newComment.parent_id) {
            setComments(prev => prev.map(c => {
                if (c.id === newComment.parent_id) {
                    return { ...c, replies: [...(c.replies || []), newComment] };
                }
                return c;
            }));
        } else {
            setComments(prev => [...prev, newComment]);
        }

        setCommentText('');
        setReplyTo(null);
        setIsCommentSpoiler(false);
        setSendingComment(true);

        try {
            await socialService.addComment(user.id, selectedClub.id, originalText, newComment.parent_id, isCommentSpoiler, optimisticId);
            // Clear SWR cache for the next real fetch
            localStorage.removeItem(`data_club_comments_${selectedClub.id}`);
            
            // Subtle reload in the background to sync with real DB data
            setTimeout(() => {
                if (selectedClub?.id) fetchComments(selectedClub.id);
            }, 1000);
        } catch (err: any) {
            console.error('Error publishing comment:', err);
            // Rollback optimistic update
            if (newComment.parent_id) {
                setComments(prev => prev.map(c => {
                    if (c.id === newComment.parent_id) {
                        return { ...c, replies: (c.replies || []).filter(r => r.id !== optimisticId) };
                    }
                    return c;
                }));
            } else {
                setComments(prev => prev.filter(c => c.id !== optimisticId));
            }
            setCommentText(originalText);
            alert('Não foi possível publicar seu comentário. Por favor, tente novamente.');
        } finally {
            setSendingComment(false);
        }
    };

    const handleReact = async (commentId: string, emoji: string) => {
        if (!user) return;

        // Optimistic React Logic
        setComments(prev => {
            const updateComment = (c: ClubComment): ClubComment => {
                if (c.id === commentId) {
                    const currentReactions = [...(c.reactions || [])];
                    const existingReaction = currentReactions.find(r => r.reacted);
                    
                    let newReactions = currentReactions.map(r => ({ ...r }));

                    // If user already had a reaction
                    if (existingReaction) {
                        // Case 1: Same emoji -> Remove it (Toggle OFF)
                        if (existingReaction.emoji === emoji) {
                            newReactions = newReactions.map(r => {
                                if (r.emoji === emoji) return { ...r, count: Math.max(0, r.count - 1), reacted: false };
                                return r;
                            }).filter(r => r.count > 0);
                        } 
                        // Case 2: Different emoji -> Swap
                        else {
                            // Decrement old
                            newReactions = newReactions.map(r => {
                                if (r.emoji === existingReaction.emoji) return { ...r, count: Math.max(0, r.count - 1), reacted: false };
                                return r;
                            }).filter(r => r.count > 0);

                            // Increment/Add new
                            const targetIdx = newReactions.findIndex(r => r.emoji === emoji);
                            if (targetIdx >= 0) {
                                newReactions[targetIdx] = { ...newReactions[targetIdx], count: newReactions[targetIdx].count + 1, reacted: true };
                            } else {
                                newReactions.push({ emoji, count: 1, reacted: true });
                            }
                        }
                    } 
                    // Case 3: No previous reaction -> Toggle ON
                    else {
                        const targetIdx = newReactions.findIndex(r => r.emoji === emoji);
                        if (targetIdx >= 0) {
                            newReactions[targetIdx] = { ...newReactions[targetIdx], count: newReactions[targetIdx].count + 1, reacted: true };
                        } else {
                            newReactions.push({ emoji, count: 1, reacted: true });
                        }
                    }
                    return { ...c, reactions: newReactions };
                }
                
                // Recursively update replies if any
                if (c.replies && c.replies.length > 0) {
                    return { ...c, replies: c.replies.map(updateComment) };
                }
                
                return c;
            };
            return prev.map(updateComment);
        });

        try {
            await socialService.toggleReaction(user.id, commentId, emoji);
            if (selectedClub) {
                localStorage.removeItem(`data_club_comments_${selectedClub.id}`);
            }
        } catch (err) {
            console.error("Error reacting:", err);
            // On error, the best way to rollback is to just fetch the real state
            if (selectedClub) fetchComments(selectedClub.id);
        }
    };

    const handleDeleteComment = async (commentId: string) => {
        if (!user) return;
        setCommentToDelete(commentId);
    };

    const confirmDeleteComment = async () => {
        if (!user || !commentToDelete) return;
        
        await socialService.deleteComment(commentToDelete, user.id);
        if (selectedClub) {
            localStorage.removeItem(`data_club_comments_${selectedClub.id}`);
            fetchComments(selectedClub.id);
        }
        setCommentToDelete(null);
    };

    // ── Delete Club (creator only) ────────────────────────────────────────────────

    const handleDeleteClub = async (clubId: string) => {
        if (!user) return;
        setClubToDelete(clubId);
    };

    const confirmDeleteClub = async () => {
        if (!user || !clubToDelete) return;
        // With cascade logic in D1 backend, we just delete the club
        await dbManager.from('book_clubs', user.id).delete(clubToDelete);
        cacheManager.clear(`data_clubs_${user.id}`);
        fetchClubs();
        setClubToDelete(null);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Max deadline helper: 60 days from today
    const maxDeadline = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const minDeadline = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────

    const renderContent = () => {
        // ── LANDING ──────────────────────────────────────────────────────────────
        if (view === 'landing') {
        return (
            <div className="max-w-5xl mx-auto space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-600 pb-24">
                <div className="space-y-4">
                    <AppBackButton fallbackTo="/" />
                    <StudyPageHeader
                        tourKey="book_club"
                        title={t('hubs.community.clubs.label', 'Clube do Livro')}
                        actions={
                          <>
                          <CommunityLinks current="clubs" />
                          </>
                        }
                    />
                </div>

                <div className="text-center space-y-4">
                    <div className="w-20 h-20 bg-tint-butter rounded-full flex items-center justify-center text-4xl mx-auto animate-[bounce_3s_ease-in-out_infinite]">
                        🤝
                    </div>
                    <h2 className="font-display text-3xl md:text-5xl font-light text-ink tracking-[-0.045em] leading-[1.05]">
                        {t('community.book_club.landing.title_start', 'Aprender é melhor ')} <br className="hidden md:block" />
                        <span className="text-primary">{t('community.book_club.landing.title_highlight', 'quando é junto!')}</span>
                    </h2>
                    <p className="text-muted text-base leading-relaxed max-w-xl mx-auto">
                        {t('community.book_club.landing.description', 'No Clube do Livro, você e seus amigos mergulham na mesma história, compartilham descobertas e debatem cada capítulo. A jornada fica mais leve e o aprendizado muito mais rápido!')}
                    </p>
                </div>

                {/* Role Cards */}
                <div data-tour="club-roles" className="grid md:grid-cols-2 gap-5">
                    <div className="bg-tint-peach text-ink rounded-[32px] p-8 space-y-4 transition-transform duration-500 hover:-translate-y-1">
                        <div className="text-4xl">🏰</div>
                        <h3 className="font-display text-2xl font-light tracking-[-0.04em] text-ink">{t('community.book_club.landing.creator_title', 'Seja um Criador')}</h3>
                        <p className="text-ink/70 text-sm leading-relaxed">
                            {t('community.book_club.landing.creator_desc1', 'Escolha a obra, convoque sua \"tropa\" de até 5 amigos e dite o ritmo da jornada.')}
                            <strong className="text-ink font-medium">{t('community.book_club.landing.creator_desc2', ' Você lidera o desafio!')}</strong>
                        </p>
                    </div>
                    <div className="bg-tint-sage text-ink rounded-[32px] p-8 space-y-4 transition-transform duration-500 hover:-translate-y-1">
                        <div className="text-4xl">📖</div>
                        <h3 className="font-display text-2xl font-light tracking-[-0.04em] text-ink">{t('community.book_club.landing.member_title', 'Seja um Participante')}</h3>
                        <p className="text-ink/70 text-sm leading-relaxed">
                            {t('community.book_club.landing.member_desc1', 'Aceite convites, acompanhe o ritmo do grupo e compartilhe suas percepções com')}
                            <strong className="text-ink font-medium">{t('community.book_club.landing.member_desc2', ' quem está na mesma página que você.')}</strong>
                        </p>
                    </div>
                </div>

                <div className="text-center">
                    <button
                        data-tour="club-cta"
                        onClick={() => { setView('list'); }}
                        className="bg-primary text-slate-900 font-medium h-12 px-8 rounded-full text-sm hover:opacity-90 active:scale-95 transition-all"
                    >
                        {t('community.book_club.landing.view_clubs', 'Ver meus Clubes →')}
                    </button>
                </div>
            </div>
        );
    }

    // ── COMMENT FEED (pos-deadline) ───────────────────────────────────────────
    if (view === 'comment-feed' && selectedClub) {
        const trueCreatorProfile = members.find(m => m.user_id === selectedClub?.creator_id)?.profile || creatorProfile;

        return (
            <div className="max-w-2xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <button onClick={() => setView('list')} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors">
                        <ArrowLeft size={20} className="dark:text-white" />
                    </button>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            {daysLeft(selectedClub.deadline) > 0 ? (
                                <span className="text-[13px] font-medium text-primary bg-primary/10 px-3 py-1 rounded-full flex items-center gap-1">
                                    <Clock size={12} /> 🔥 {t('community.book_club.club.in_progress', 'Em Andamento')}
                                </span>
                            ) : (
                                <span className="text-[13px] font-medium text-ok bg-ok/10 px-3 py-1 rounded-full">
                                    ✓ {t('community.book_club.club.completed', 'Concluído')}
                                </span>
                            )}
                        </div>
                        <h2 className="text-lg font-semibold dark:text-ink truncate mt-1">{selectedClub.book_title}</h2>
                    </div>

                    <div className="flex -space-x-2">
                        <UserAvatar url={trueCreatorProfile?.avatar_url} name={trueCreatorProfile?.display_name || trueCreatorProfile?.username || '?'} size={28} />
                        {members.filter(m => m.status === 'accepted' && m.user_id !== selectedClub.creator_id).slice(0, 3).map(m => (
                            <UserAvatar key={m.id} url={m.profile?.avatar_url} name={m.profile?.display_name || m.profile?.username || '?'} size={28} />
                        ))}
                        {members.filter(m => m.status === 'accepted' && m.user_id !== selectedClub.creator_id).length > 3 && (
                            <div className="w-7 h-7 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-[8px] font-semibold text-slate-500 border border-white dark:border-white/[0.06] z-10">
                                +{members.filter(m => m.status === 'accepted' && m.user_id !== selectedClub.creator_id).length - 3}
                            </div>
                        )}
                    </div>
                </div>

                {/* Book card */}
                <div className="flex items-center gap-4 bg-slate-100 dark:bg-slate-800 rounded-2xl p-4">
                    <div className="w-14 h-20 shrink-0">
                        <BookCover 
                            title={selectedClub.book_title} 
                            author={selectedClub.book_author} 
                            coverUrl={selectedClub.book_cover} 
                            isSmall 
                        />
                    </div>
                    <div>
                        <p className="font-semibold dark:text-ink text-sm">{selectedClub.book_title}</p>
                        <p className="text-slate-500 text-xs">{selectedClub.book_author}</p>
                        <p className="text-slate-400 text-xs mt-1">{t('community.book_club.club.deadline', 'Prazo: ')}{formatDate(selectedClub.deadline, i18n.language)}</p>
                        <p className="text-ok text-xs font-bold mt-0.5">{members.filter(m => m.status === 'accepted').length + (trueCreatorProfile ? 1 : 0)} {t('community.book_club.club.participants', 'Participantes').toLowerCase()}</p>
                    </div>
                </div>

                {/* Members / Participantes */}
                <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-2xl p-5 space-y-4">
                    <h3 className="font-medium dark:text-ink text-sm">
                        {t('community.book_club.club.participants', 'Participantes')} ({members.filter(m => m.status === 'accepted').length + (trueCreatorProfile ? 1 : 0)}/5)
                    </h3>
                    <div className="space-y-3">
                        {/* Creator row */}
                        {trueCreatorProfile && (
                            <div className="flex items-center gap-3">
                                <div onClick={() => trueCreatorProfile?.username && navigate(`/profile/${trueCreatorProfile.username}`)} className="cursor-pointer">
                                    <UserAvatar url={trueCreatorProfile?.avatar_url} name={trueCreatorProfile?.display_name || trueCreatorProfile?.username || 'Participante'} />
                                </div>
                                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => trueCreatorProfile?.username && navigate(`/profile/${trueCreatorProfile.username}`)}>
                                    <p className="text-sm font-bold dark:text-ink truncate">{trueCreatorProfile?.display_name || trueCreatorProfile?.username || 'Participante'}</p>
                                    {trueCreatorProfile?.username && <p className="text-[11px] text-slate-400 truncate">@{trueCreatorProfile.username}</p>}
                                </div>
                                <span className="text-[10px] font-medium text-primary bg-primary/10 px-3 py-1 rounded-full">{t('community.book_club.club.creator', 'Criador')}</span>
                            </div>
                        )}

                        {/* Members row up to 4 limit to fit 5 max incl creator */}
                        {members
                            .filter(m => m.user_id !== selectedClub.creator_id && m.status !== 'declined')
                            .slice(0, 4)
                            .map(m => (
                                <div key={m.id} className="flex items-center gap-3">
                                    <div className="cursor-pointer" onClick={() => m.profile?.username && navigate(`/profile/${m.profile.username}`)}>
                                        <UserAvatar url={m.profile?.avatar_url} name={m.profile?.display_name || m.profile?.username || '?'} />
                                    </div>
                                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => m.profile?.username && navigate(`/profile/${m.profile.username}`)}>
                                        <p className="text-sm font-bold dark:text-ink truncate">{m.profile?.display_name || m.profile?.username || 'Usuário'}</p>
                                        {m.profile?.username && <p className="text-[11px] text-slate-400 truncate">@{m.profile.username}</p>}
                                    </div>
                                    <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 border border-black/[0.06] dark:border-slate-700 px-3 py-1 rounded-full">{t('community.book_club.club.guest', 'Convidado')}</span>
                                </div>
                            ))}
                    </div>
                </div>

                {/* Comment Input */}
                <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-2xl p-4 space-y-3">
                    {replyTo && (
                        <div className="flex items-center justify-between bg-slate-100 dark:bg-slate-800 rounded-xl px-3 py-2">
                            <span className="text-xs text-slate-500">
                                ↩ Respondendo <strong>{replyTo.profile?.display_name}</strong>: "{replyTo.content.slice(0, 40)}..."
                            </span>
                            <button onClick={() => setReplyTo(null)}><X size={14} className="text-slate-400" /></button>
                        </div>
                    )}
                    <div className="relative">
                        <textarea
                            value={commentText}
                            onChange={e => {
                                setCommentText(e.target.value.slice(0, 500));
                                handleCursorChange(e.target.selectionStart);
                            }}
                            onKeyDown={handleKeyDown}
                            onKeyUp={(e) => handleCursorChange((e.target as HTMLTextAreaElement).selectionStart)}
                            onMouseUp={(e) => handleCursorChange((e.target as HTMLTextAreaElement).selectionStart)}
                            placeholder={t('community.book_club.club.comment_placeholder', 'Escreva seu resumo ou comentário sobre o livro... (máx. 500 caracteres)')}
                            className="w-full text-sm bg-transparent dark:text-ink resize-none outline-none min-h-[80px] placeholder:text-slate-400 custom-scrollbar"
                        />
                        <MentionDropdown
                            results={mentionResults}
                            selectedIndex={selectedIndex}
                            onSelect={handleSelectMention}
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <span className="text-[11px] text-slate-400">{commentText.length}/500</span>
                            <label className="flex items-center gap-2 cursor-pointer group">
                                <div className="relative flex items-center">
                                    <input 
                                        type="checkbox" 
                                        checked={isCommentSpoiler}
                                        onChange={e => setIsCommentSpoiler(e.target.checked)}
                                        className="sr-only"
                                    />
                                    <div className={`w-8 h-4 rounded-full transition-colors ${isCommentSpoiler ? 'bg-warn' : 'bg-slate-200 dark:bg-slate-700'}`} />
                                    <div className={`absolute w-3 h-3 rounded-full bg-surface transition-transform ${isCommentSpoiler ? 'translate-x-[17px]' : 'translate-x-[3px]'}`} />
                                </div>
                                <span className={`text-[11px] font-bold transition-colors ${isCommentSpoiler ? 'text-warn' : 'text-slate-500'}`}>Spoiler</span>
                            </label>
                        </div>
                        <button
                            onClick={handleSendComment}
                            disabled={!commentText.trim() || sendingComment}
                            className="flex items-center gap-2 bg-primary text-slate-900 text-[11px] font-semibold px-4 py-2 rounded-xl hover:scale-105 active:scale-95 transition-all disabled:opacity-40"
                        >
                            {sendingComment ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                            {t('community.book_club.club.publish', 'Publicar')}
                        </button>
                    </div>
                </div>

                {/* Comments */}
                <div className="space-y-5">
                    {comments.length === 0 ? (
                        <div className="text-center py-10 text-slate-400">
                            <MessageCircle size={40} className="mx-auto mb-3 opacity-30" />
                            <p className="font-semibold text-sm">{t('community.book_club.club.first_comment', 'Seja o primeiro a comentar!')}</p>
                        </div>
                    ) : (
                        comments.map(c => (
                            <Comment
                                key={c.id}
                                comment={c}
                                userId={user?.id || ''}
                                onReact={handleReact}
                                onReply={setReplyTo}
                                onDelete={handleDeleteComment}
                            />
                        ))
                    )}
                </div>
                </div>
            );
        }

        // ── CLUB DETAIL ───────────────────────────────────────────────────────────
        if (view === 'detail' && selectedClub) {
        const isCreator = selectedClub.creator_id === user?.id;
        const days = daysLeft(selectedClub.deadline);
        const trueCreatorProfile = members.find(m => m.user_id === selectedClub?.creator_id)?.profile || creatorProfile;

        return (
            <div className="max-w-2xl mx-auto space-y-6">
                <div className="flex items-center gap-4">
                    <button onClick={() => setView('list')} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors">
                        <ArrowLeft size={20} className="dark:text-white" />
                    </button>
                    <h2 className="text-xl font-medium text-ink tracking-[-0.02em] truncate">{selectedClub.book_title}</h2>
                </div>

                {/* Book Info */}
                <div className="flex items-center gap-4 bg-slate-100 dark:bg-slate-800 rounded-2xl p-5">
                    <div className="w-16 h-24 shrink-0 overflow-hidden rounded-xl">
                        <BookCover 
                            title={selectedClub.book_title} 
                            author={selectedClub.book_author} 
                            coverUrl={selectedClub.book_cover} 
                            isSmall 
                        />
                    </div>
                    <div className="space-y-1 min-w-0">
                        <p className="font-semibold dark:text-ink">{selectedClub.book_title}</p>
                        <p className="text-slate-500 text-sm">{selectedClub.book_author}</p>
                        {selectedClub.book_language && (
                            <p className="text-xs text-slate-400 flex items-center gap-1"><Globe size={10} /> {selectedClub.book_language}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                            <div className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${days > 7 ? 'bg-ok/10 text-ok' : days > 0 ? 'bg-warn/10 text-warn' : 'bg-bad/10 text-bad'}`}>
                                <Clock size={10} /> {days > 0 ? `${days} dias restantes` : 'Prazo encerrado'}
                            </div>
                            {isCreator && (
                                <div className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-primary/10 text-primary">
                                    <Crown size={10} /> {t('community.book_club.club.creator', 'Criador')}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Accept/Decline for pending members */}
                {selectedClub.role === 'membro' && selectedClub.member_status === 'pending' && (
                    <div className="bg-tint-butter dark:bg-tint-butter/20 border border-tint-butter dark:border-tint-butter rounded-2xl p-5 space-y-3">
                        <p className="font-semibold text-warn dark:text-warn text-sm"><T k="community.book_club.club.invited_title" /></p>
                        <p className="text-sm text-warn dark:text-warn">
                            Ao aceitar, o livro <strong>"{selectedClub.book_title}"</strong> será adicionado à sua estante com a tag <strong><T k="community.book_club.title" /></strong>.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => handleAcceptInvite(selectedClub)}
                                className="flex items-center gap-2 bg-primary text-slate-900 font-semibold text-xs px-5 py-2.5 rounded-xl hover:scale-105 active:scale-95 transition-all"
                            >
                                <Check size={14} /> Aceitar
                            </button>
                            <button
                                onClick={() => handleDeclineInvite(selectedClub)}
                                className="flex items-center gap-2 bg-bad/10 text-bad font-semibold text-xs px-5 py-2.5 rounded-xl hover:scale-105 active:scale-95 transition-all"
                            >
                                <X size={14} /> Recusar
                            </button>
                        </div>
                    </div>
                )}

                {/* Members */}
                <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-2xl p-5 space-y-4">
                    <h3 className="font-normal dark:text-ink text-[10px] uppercase font-mono tracking-[0.14em] text-slate-400">
                        {t('community.book_club.club.participants', 'Participantes')} ({members.filter(m => m.status === 'accepted').length + (trueCreatorProfile ? 1 : 0)}/5)
                    </h3>
                    <div className="space-y-3">
                        {/* Creator row — real profile */}
                        {trueCreatorProfile && (
                            <div className="flex items-center gap-3">
                                <div className="cursor-pointer" onClick={() => trueCreatorProfile?.username && navigate(`/profile/${trueCreatorProfile.username}`)}>
                                    <UserAvatar url={trueCreatorProfile?.avatar_url} name={trueCreatorProfile?.display_name || trueCreatorProfile?.username || 'Participante'} />
                                </div>
                                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => trueCreatorProfile?.username && navigate(`/profile/${trueCreatorProfile.username}`)}>
                                    <p className="text-sm font-bold dark:text-ink truncate">{trueCreatorProfile?.display_name || trueCreatorProfile?.username || 'Participante'}</p>
                                    {trueCreatorProfile?.username && <p className="text-[11px] text-slate-400 truncate">@{trueCreatorProfile.username}</p>}
                                </div>
                                <span className="text-[10px] font-semibold text-primary bg-primary/10 px-2 py-1 rounded-full">{t('community.book_club.club.creator', 'Criador')}</span>
                            </div>
                        )}

                        {members
                            .filter(m => m.user_id !== selectedClub.creator_id && m.status !== 'declined')
                            .slice(0, 4)
                            .map(m => (
                                <div key={m.id} className="flex items-center gap-3">
                                    <div className="cursor-pointer" onClick={() => m.profile?.username && navigate(`/profile/${m.profile.username}`)}>
                                        <UserAvatar url={m.profile?.avatar_url} name={m.profile?.display_name || m.profile?.username || '?'} />
                                    </div>
                                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => m.profile?.username && navigate(`/profile/${m.profile.username}`)}>
                                        <p className="text-sm font-bold dark:text-ink truncate">{m.profile?.display_name || m.profile?.username || 'Participante'}</p>
                                        {m.profile?.username && <p className="text-[11px] text-slate-400 truncate">@{m.profile.username}</p>}
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 border border-black/[0.06] dark:border-slate-700 px-3 py-1 rounded-full">
                                            Convidado
                                        </span>
                                        <span className={`text-[10px] font-semibold px-2 py-1 rounded-full ${m.status === 'accepted' ? 'bg-ok/10 text-ok' : 'bg-warn/10 text-warn'}`}>
                                            {m.status === 'accepted' ? '✓ Confirmado' : '⏳ Aguardando...'}
                                        </span>
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>

                {daysLeft(selectedClub.deadline) > 0 && selectedClub.member_status === 'accepted' && (
                    <div className="text-center py-4">
                        <button 
                            onClick={() => { setView('comment-feed'); fetchComments(selectedClub.id); }}
                            className="bg-primary/10 text-primary hover:bg-primary/20 font-medium text-[11px] px-6 py-3 rounded-2xl transition-all"
                        >
                            Ver Feed de Comentários →
                        </button>
                    </div>
                )}
            </div>
        );
    }

    // ── CLUB LIST ─────────────────────────────────────────────────────────────

    const pending = clubs.filter(c => c.role === 'membro' && c.member_status === 'pending');
    const active = clubs.filter(c => c.status === 'ativo' && !(c.role === 'membro' && c.member_status === 'pending'));
    const finished = clubs.filter(c => c.status === 'finalizado');

    return (
        <div className="max-w-3xl mx-auto space-y-8">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button onClick={() => setView('landing')} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors">
                        <ChevronLeft size={20} className="dark:text-white" />
                    </button>
                    <h1 className="text-2xl font-medium dark:text-ink tracking-[-0.03em]">{t('community.book_club.clubs.title', 'Clube do livro')}</h1>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 bg-primary text-slate-900 font-semibold text-xs px-5 py-3 rounded-2xl hover:scale-105 active:scale-95 transition-all"
                >
                    <Plus size={16} /> {t('community.book_club.clubs.create', 'Criar Clube')}
                </button>
            </div>

            <WatchdogGuard isLoading={loading} onRetry={fetchClubs}>
            {loading ? (
                <FeedSkeleton />
            ) : clubs.length === 0 ? (
                <div className="text-center py-20 space-y-4">
                    <div className="text-6xl">📚</div>
                    <h2 className="text-lg font-semibold dark:text-ink">{t('community.book_club.clubs.empty_title', 'Nenhum clube ainda')}</h2>
                    <p className="text-slate-500 text-sm">{t('community.book_club.clubs.empty_desc', 'Crie seu primeiro clube ou aguarde um convite de um amigo.')}</p>
                    <button onClick={() => setShowCreateModal(true)} className="bg-primary text-slate-900 font-semibold text-xs px-6 py-3 rounded-2xl hover:scale-105 active:scale-95 transition-all mt-2">
                        <Plus size={14} className="inline mr-2" /> {t('community.book_club.clubs.create', 'Criar Clube')}
                    </button>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Pending Invites */}
                    {pending.length > 0 && (
                        <div className="space-y-3">
                            <h2 className="text-xs font-normal text-warn uppercase font-mono tracking-[0.14em] flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-warn animate-pulse" />
                                {t('community.book_club.clubs.pending', 'Convites Pendentes')} ({pending.length})
                            </h2>
                            {pending.map(club => (
                                <ClubCard key={club.id} club={club} onClick={() => fetchClubDetail(club)} />
                            ))}
                        </div>
                    )}

                    {/* Active */}
                    {active.length > 0 && (
                        <div className="space-y-3">
                            <h2 className="text-xs font-normal text-ok uppercase font-mono tracking-[0.14em]">{t('community.book_club.clubs.active', 'Ativos')} ({active.length})
                            </h2>
                            {active.map(club => (
                                <ClubCard key={club.id} club={club} onClick={() => fetchClubDetail(club)}
                                    onDelete={club.role === 'criador' ? handleDeleteClub : undefined} />
                            ))}
                        </div>
                    )}

                    {/* Finished */}
                    {finished.length > 0 && (
                        <div className="space-y-3">
                            <h2 className="text-xs font-normal text-slate-400 uppercase font-mono tracking-[0.14em]">
                                {t('community.book_club.clubs.finished', 'Concluídos — Feed de Comentários')} ({finished.length})
                            </h2>
                            {finished.map(club => (
                                <ClubCard key={club.id} club={club} onClick={() => fetchClubDetail(club)} finished
                                    onDelete={club.role === 'criador' ? handleDeleteClub : undefined} />
                            ))}
                        </div>
                    )}
                </div>
            )}
            </WatchdogGuard>
        </div>
    );
    };

    return (
        <>
            {renderContent()}

            {/* ── Create Club Modal ────────────────────────────────────────────── */}
            {showCreateModal && ReactDOM.createPortal(
                <div
                    className="flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-950/80 backdrop-blur-xl"
                    style={{ position: 'fixed', inset: 0, zIndex: 9999 }}
                    onClick={(e) => { if (e.target === e.currentTarget) setShowCreateModal(false); }}
                >
                    <div className="bg-surface rounded-t-[2.5rem] sm:rounded-[28px] w-full max-w-xl max-h-[92vh] overflow-y-auto">
                        <div className="p-6 sm:p-8 space-y-6">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-medium text-ink tracking-[-0.02em]">{t('community.book_club.club.create_club_btn', 'Criar Clube do Livro')}</h2>
                                <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl">
                                    <X size={20} className="dark:text-slate-400" />
                                </button>
                            </div>

                            {/* Step 1: Search Book */}
                            <div className="space-y-4">
                                <label className="text-[10px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em] block">{t('community.book_club.club.step1', '1. Escolha um livro da Biblioteca')}</label>
                                
                                <div className="space-y-2">
                                    <label className="text-[9px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em] block px-1">{t('community.book_club.club.select_lang', 'Selecione o Idioma')}</label>
                                    <select
                                        value={selectedLanguage}
                                        onChange={e => setSelectedLanguage(e.target.value)}
                                        className="w-full px-4 py-3 bg-slate-100 dark:bg-slate-800 rounded-xl text-sm dark:text-ink outline-none border-none ring-2 ring-transparent focus:ring-primary/20 transition-all font-bold appearance-none cursor-pointer"
                                    >
                                        <option value="">{t('community.book_club.club.all_langs', 'Todos os idiomas')}</option>
                                        {availableLanguages.map(lang => (
                                            <option key={lang} value={lang}>
                                                {ISO_FLAG_MAP[lang] || '🌐'} {getLanguageName(lang)}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="relative">
                                    <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        value={bookSearch}
                                        onChange={e => setBookSearch(e.target.value)}
                                        placeholder={t('community.book_club.club.search_book', 'Busque por título ou autor...')}
                                        className="w-full pl-10 pr-4 py-3 bg-slate-100 dark:bg-slate-800 rounded-xl text-sm dark:text-ink outline-none"
                                    />
                                </div>

                                {selectedBook && (
                                    <div className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-2xl p-3 animate-in zoom-in-95">
                                        <div className="w-10 h-14 flex-shrink-0">
                                            <BookCover title={selectedBook.title} coverUrl={selectedBook.cover} isSmall />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold dark:text-ink truncate">{selectedBook.title}</p>
                                            <p className="text-xs text-slate-500 truncate">{selectedBook.author}</p>
                                        </div>
                                        <button onClick={() => setSelectedBook(null)} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-colors"><X size={14} className="text-slate-400" /></button>
                                    </div>
                                )}

                                {loadingLibrary ? (
                                    <div className="flex justify-center py-4"><Loader2 className="animate-spin text-primary" size={24} /></div>
                                ) : bookSearch.length >= 2 && !selectedBook ? (
                                    <div className="max-h-56 overflow-y-auto space-y-1 custom-scrollbar">
                                        {filteredBooks.slice(0, 20).map(b => (
                                            <button key={b.id} onClick={() => { setSelectedBook(b); setBookSearch(''); }}
                                                className="w-full flex items-center gap-3 p-3 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors text-left group">
                                                <div className="w-8 h-11 flex-shrink-0">
                                                    <BookCover title={b.title} coverUrl={b.cover} isSmall />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-bold dark:text-ink truncate">{b.title}</p>
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                        <p className="text-xs text-slate-500 truncate">{b.author}</p>
                                                        {b.language && (
                                                            <span className="flex-shrink-0 text-[9px] font-medium bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                                                                {langLabel(b.language)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </button>
                                        ))}
                                        {filteredBooks.length === 0 && <p className="text-center text-sm text-slate-400 py-4">Nenhum livro encontrado</p>}
                                    </div>
                                ) : null}
                            </div>

                            {/* Step 2: Deadline */}
                            <div className="space-y-3">
                                <label className="text-[10px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em] block">{t('community.book_club.club.step2', '2. Defina o prazo e horário sugerido')}</label>
                                <div className="flex gap-3">
                                    <div className="relative flex-1">
                                        <Calendar size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            type="date"
                                            lang={i18n.language || 'en'}
                                            value={deadline}
                                            onChange={e => setDeadline(e.target.value)}
                                            min={minDeadline}
                                            max={maxDeadline}
                                            className="w-full pl-10 pr-4 py-3 bg-slate-100 dark:bg-slate-800 rounded-xl text-sm dark:text-ink outline-none"
                                        />
                                    </div>
                                    <div className="relative w-32">
                                        <Clock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            type="time"
                                            lang={i18n.language || 'en'}
                                            value={deadlineTime}
                                            onChange={e => setDeadlineTime(e.target.value)}
                                            className="w-full pl-10 pr-4 py-3 bg-slate-100 dark:bg-slate-800 rounded-xl text-sm dark:text-ink outline-none"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Step 3: Invite Friends */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <label className="text-[10px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em]">
                                        {t('community.book_club.club.step3', '3. Convide até 5 amigos')} (até {5 - selectedFriendIds.length} restantes)
                                    </label>
                                </div>
                                {friends.length === 0 ? (
                                    <p className="text-sm text-slate-400 text-center py-4"><T k="ui.club.noFriends" /></p>
                                ) : (
                                    <div className="space-y-2 max-h-52 overflow-y-auto custom-scrollbar">
                                        {friends.map((f) => {
                                            // DataContext returns friends as flat Profile & { friendshipId }
                                            const friendId = f.id;
                                            const isSelected = selectedFriendIds.includes(friendId);
                                            const isDisabled = !isSelected && selectedFriendIds.length >= 5;

                                            return (
                                                <button
                                                    key={f.friendshipId}
                                                    disabled={isDisabled}
                                                    onClick={() => {
                                                        if (isSelected) {
                                                            setSelectedFriendIds(prev => prev.filter(id => id !== friendId));
                                                        } else if (selectedFriendIds.length < 5) {
                                                            setSelectedFriendIds(prev => [...prev, friendId]);
                                                        }
                                                    }}
                                                    className={`w-full flex items-center gap-3 p-3 rounded-2xl border transition-all ${isSelected ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-slate-100 dark:hover:bg-slate-800'} ${isDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                                                >
                                                    <UserAvatar url={f.avatar_url} name={f.display_name || f.username || '?'} />
                                                    <div className="flex-1 text-left min-w-0">
                                                        <p className="text-sm font-bold dark:text-ink truncate">{f.display_name || f.username}</p>
                                                        <p className="text-xs text-slate-400 truncate">@{f.username}</p>
                                                    </div>
                                                    {isSelected && <Check size={16} className="text-primary flex-shrink-0" />}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Submit */}
                            <button
                                onClick={handleCreateClub}
                                disabled={!selectedBook || !deadline || selectedFriendIds.length === 0 || creating}
                                className="w-full bg-primary text-slate-900 font-semibold py-4 rounded-2xl hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-40 flex items-center justify-center gap-2"
                            >
                                {creating ? <><Loader2 size={16} className="animate-spin" /> Criando...</> : `🚀 ${t('community.book_club.clubs.create', 'Criar Clube')} do Livro`}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* ── Internal Modals ────────────────────────────────────────────── */}
            
            {/* Delete Comment Modal */}
            {commentToDelete && (
                <div 
                    className="fixed flex inset-0 items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xl z-[99999]"
                    onClick={(e) => { if (e.target === e.currentTarget) setCommentToDelete(null); }}
                >
                    <div className="bg-surface rounded-[24px] p-6 max-w-sm w-full space-y-5 animate-in zoom-in-95">
                        <div className="w-16 h-16 bg-bad/10 text-bad rounded-full flex items-center justify-center mx-auto mb-2">
                            <Trash2 size={24} />
                        </div>
                        <h3 className="text-xl font-semibold text-center dark:text-ink">{t('community.book_club.club.delete_comment', 'Excluir comentário?')}</h3>
                        <p className="text-center text-slate-500 text-sm"><T k="ui.club.deleteComment" /></p>
                        <div className="flex gap-3 pt-2">
                            <button onClick={() => setCommentToDelete(null)} className="flex-1 py-3 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold rounded-xl text-sm hover:scale-[1.02] transition-transform">
                                {t('common.cancel', 'Cancelar')}
                            </button>
                            <button onClick={confirmDeleteComment} className="flex-1 py-3 bg-bad text-surface font-bold rounded-xl text-sm hover:scale-[1.02] transition-transform">
                                {t('community.book_club.club.delete', 'Excluir')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Club Modal */}
            {clubToDelete && (
                <div 
                    className="fixed flex inset-0 items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xl z-[99999]"
                    onClick={(e) => { if (e.target === e.currentTarget) setClubToDelete(null); }}
                >
                    <div className="bg-surface rounded-[24px] p-6 max-w-sm w-full space-y-5 animate-in zoom-in-95">
                        <div className="w-16 h-16 bg-bad/10 text-bad rounded-full flex items-center justify-center mx-auto mb-2">
                            <Trash2 size={24} />
                        </div>
                        <h3 className="text-xl font-semibold text-center dark:text-ink">{t('community.book_club.club.delete_club', 'Excluir Clube?')}</h3>
                        <p className="text-center text-slate-500 text-sm"><T k="ui.club.endGroup" /></p>
                        <div className="flex gap-3 pt-2">
                            <button onClick={() => setClubToDelete(null)} className="flex-1 py-3 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold rounded-xl text-sm hover:scale-[1.02] transition-transform">
                                {t('common.cancel', 'Cancelar')}
                            </button>
                            <button onClick={confirmDeleteClub} className="flex-1 py-3 bg-bad text-surface font-bold rounded-xl text-sm hover:scale-[1.02] transition-transform">
                                {t('community.book_club.club.delete', 'Sim, excluir')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Premium Limit Modal */}
            {showLimitModal && (
                <div 
                    className="fixed flex inset-0 items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xl z-[99999]"
                    onClick={(e) => { if (e.target === e.currentTarget) setShowLimitModal(false); }}
                >
                    <div className="bg-gradient-to-b from-warn/10 to-transparent dark:from-warn/5 bg-surface rounded-[24px] p-6 max-w-sm w-full border border-warn/20 space-y-5 animate-in zoom-in-95">
                        <div className="w-16 h-16 bg-warn/10 text-warn rounded-full flex items-center justify-center mx-auto mb-2">
                            <Trophy size={28} />
                        </div>
                        <h3 className="text-xl font-semibold text-center text-warn dark:text-warn"><T k="ui.club.limit" /></h3>
                        <p className="text-center text-slate-500 text-sm leading-relaxed">
                            Usuários do <strong className="text-warn">Free</strong> podem participar de até <strong className="text-slate-700 dark:text-ink">1 Clube do Livro simultâneo</strong>. Para criar ou participar de clubes ilimitados e sem barreiras, melhore para o Premium!
                        </p>
                        <div className="pt-2">
                            <button onClick={() => setShowLimitModal(false)} className="w-full py-4 bg-warn text-surface font-semibold rounded-xl text-sm hover:scale-[1.02] transition-transform">
                                Entendi
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </>
    );
};

const ClubCard: React.FC<{ club: BookClub; onClick: () => void; finished?: boolean; onDelete?: (id: string) => void }> = ({ club, onClick, finished = false, onDelete }) => {
    const { t } = useTranslation();
    const days = daysLeft(club.deadline);

    return (
        <div className="relative">
            <button onClick={onClick} className="w-full flex items-center gap-4 bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-2xl p-4 hover:border-primary/80 hover:shadow-md transition-all text-left group pr-14">
                <div className="w-12 h-16 flex-shrink-0">
                    <BookCover title={club.book_title} coverUrl={club.book_cover} isSmall />
                </div>
                <div className="flex-1 min-w-0 space-y-1">
                    <p className="font-semibold dark:text-ink text-sm truncate group-hover:text-primary transition-colors">{club.book_title}</p>
                    <p className="text-slate-500 text-xs truncate">{club.book_author}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                        {club.role === 'criador' ? (
                            <span className="text-[10px] font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Crown size={8} /> {t('community.book_club.club.creator', 'Criador')}
                            </span>
                        ) : (
                            <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <UserCheck size={8} />
                                {club.member_status === 'pending' ? t('community.book_club.club.pending_invite', '⏳ Convite pendente') : t('community.book_club.club.member', 'Membro')}
                            </span>
                        )}
                        {!finished && (
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${days > 7 ? 'text-ok bg-ok/10' : days > 0 ? 'text-warn bg-warn/10' : 'text-bad bg-bad/10'}`}>
                                {days > 0 ? t('community.book_club.club.days_left_short', '{{days}}d restantes', { days }) : t('community.book_club.club.ended', 'Encerrado')}
                            </span>
                        )}
                        {!finished && club.member_status !== 'pending' && (
                            <span className="text-[10px] font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <MessageCircle size={8} /> {t('community.book_club.club.comments_open', 'Comentários abertos')}
                            </span>
                        )}
                        {finished && (
                            <span className="text-[10px] font-semibold text-ok bg-ok/10 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <MessageCircle size={8} /> {t('community.book_club.club.club_finished', 'Clube Concluído')}
                            </span>
                        )}
                    </div>
                </div>
            </button>
            {onDelete && (
                <button
                    onClick={(e) => { e.stopPropagation(); onDelete(club.id); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-2.5 text-slate-400 hover:text-bad hover:bg-bad/10 rounded-xl transition-all"
                    title="Excluir clube"
                >
                    <Trash2 size={16} />
                </button>
            )}
        </div>
    );
};

export default CommunityPage;
